import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AriaOrb } from "../components/AriaOrb";
import {
  Sparkles, GraduationCap, Mic2, MessageSquare, StickyNote, Youtube, BookOpen,
  ArrowRight, Clock, TrendingUp, TrendingDown, CalendarClock, Loader2, FileBarChart, ChevronDown, RefreshCw,
  Timer, CheckCircle2, Circle, Sunrise, FileDown, Volume2, Square,
} from "lucide-react";
import { toast } from "sonner";
import { usePlan, planProgress } from "./Plan";
import { StreakRing } from "../components/StreakRing";
import { useSpeech } from "../hooks/useSpeech";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

const LEVEL_LABEL = ["Unseen", "Exposed", "Familiar", "Understands", "Can apply", "Can teach"];

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const { speak, stop, speaking } = useSpeech();
  const [autoSpeak, setAutoSpeak] = useState(() => localStorage.getItem("aria-briefing-autospeak") === "1");

  useEffect(() => {
    (async () => {
      try {
        const [b, d] = await Promise.all([
          api.get("/home/briefing"),
          api.get("/home/dashboard"),
        ]);
        setBriefing(b.data);
        setDash(d.data);
        if (autoSpeak && b.data?.text) speak(b.data.text, "briefing");
      } catch (err) { console.error("[ARIA]", err); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAutoSpeak = () => {
    const next = !autoSpeak;
    setAutoSpeak(next);
    localStorage.setItem("aria-briefing-autospeak", next ? "1" : "0");
    if (next && briefing?.text) speak(briefing.text, "briefing");
    if (!next) stop();
  };

  const firstName = (user?.name || "Dr. Shankar").split(" ").pop();
  const totalConnected = dash ? Object.values(dash.counts || {}).reduce((a, b) => a + b, 0) : 0;
  const weakest = dash?.weak_concepts?.[0]?.concept || briefing?.weak_concepts?.[0]?.concept;

  const startReview = async (concept) => {
    if (starting) return;
    setStarting(concept || "general");
    try {
      const body = concept
        ? { target: `Review: ${concept}`, topic_focus: [concept] }
        : { target: "Daily review", topic_focus: [] };
      const { data } = await api.post("/interviews", body);
      navigate(`/app/interviews/${data.interview_id}`);
    } catch { toast.error("Could not start the review"); setStarting(null); }
  };

  return (
    <div className="h-screen overflow-y-auto">
      <div className="max-w-6xl mx-auto p-8 lg:p-12">
        <div className="flex items-center gap-4 mb-2 fade-in">
          <AriaOrb size={44} state={loading ? "thinking" : "idle"} />
          <div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight" data-testid="home-greeting">
              {greeting()}, Dr. {firstName}.
            </h1>
            <p className="text-sm text-[var(--aria-text-muted)] mt-0.5">ARIA is ready.</p>
          </div>
        </div>
        <p className="text-[var(--aria-text-muted)] max-w-2xl mb-8 leading-relaxed">
          I've connected your learning materials, notes, and interview preparation. Here's what matters today.
        </p>

        {/* Briefing */}
        <div className="aria-card aria-card-accent p-6 mb-8 relative overflow-hidden" data-testid="daily-briefing">
          <div className="absolute inset-0 bg-grad-soft opacity-40 pointer-events-none" />
          <div className="relative flex items-start gap-4">
            <div className="shrink-0 mt-1"><Sparkles size={20} className="text-[var(--aria-primary)]" /></div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--aria-primary)] font-semibold">Today's briefing</div>
                {!loading && briefing?.text && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => (speaking ? stop() : speak(briefing.text, "briefing"))}
                      data-testid="briefing-listen-button"
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors ${speaking ? "border-[var(--aria-primary)] text-[var(--aria-primary)] bg-[var(--aria-primary)]/10" : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] hover:border-[var(--aria-primary)]/60"}`}
                    >
                      {speaking ? <Square size={12} /> : <Volume2 size={12} />} {speaking ? "Stop" : "Listen"}
                    </button>
                    <button
                      onClick={toggleAutoSpeak}
                      data-testid="briefing-autospeak-toggle"
                      title="Auto-speak the briefing each time you open Home"
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors ${autoSpeak ? "border-[var(--aria-primary)] text-[var(--aria-primary)] bg-[var(--aria-primary)]/10" : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}
                    >
                      Auto {autoSpeak ? "on" : "off"}
                    </button>
                  </div>
                )}
              </div>
              {loading ? (
                <div className="h-16 flex items-center text-sm text-[var(--aria-text-muted)]">Building your briefing…</div>
              ) : (
                <p className="text-[15px] leading-relaxed whitespace-pre-line" data-testid="briefing-text">{briefing?.text}</p>
              )}
              {!loading && briefing?.has_data && (
                <button onClick={() => startReview(weakest)} disabled={!!starting} data-testid="start-review-button"
                  className="btn btn-primary mt-4 px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
                  {starting ? <Loader2 size={14} className="animate-spin" /> : null}
                  {weakest ? `Start review: ${weakest}` : "Start review"} <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Three prominent actions */}
        <div className="grid md:grid-cols-3 gap-5 mb-10 fade-in-stagger">
          <ActionCard icon={GraduationCap} title="Continue learning" text="Resume the most relevant material." onClick={() => navigate("/app/learn")} testid="action-continue-learning" />
          <ActionCard icon={Mic2} title="Prepare for interview" text="Run an adaptive mock interview." onClick={() => navigate("/app/interviews")} testid="action-prepare-interview" />
          <ActionCard icon={MessageSquare} title="Ask ARIA" text="Ask anything across your connected knowledge." onClick={() => navigate("/app/chat")} testid="action-ask-aria" />
        </div>

        <CountdownCard />

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Upcoming */}
          <Section title="Upcoming" icon={CalendarClock} testid="section-upcoming">
            {(dash?.upcoming || []).length === 0 && <Empty text="Nothing scheduled — add a deadline in Calendar." />}
            {(dash?.upcoming || []).map(d => (
              <Row key={d.deadline_id} testid={`upcoming-${d.deadline_id}`}>
                <div className="font-medium text-sm">{d.title}</div>
                <div className="text-xs text-[var(--aria-text-muted)]">{d.type} · {new Date(d.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
              </Row>
            ))}
          </Section>

          {/* Knowledge gaps */}
          <Section title="Knowledge gaps" icon={TrendingDown} testid="section-gaps">
            {(dash?.weak_concepts || []).length === 0 && <Empty text="No weak concepts flagged yet." />}
            {(dash?.weak_concepts || []).map(c => (
              <Row key={c.concept} testid={`gap-${c.concept}`} onClick={() => startReview(c.concept)}>
                <div className="flex-1">
                  <div className="font-medium text-sm">{c.concept}</div>
                  <div className="text-xs text-[var(--aria-text-muted)]">{LEVEL_LABEL[c.level] || c.level}</div>
                </div>
                <span className="text-[11px] text-[var(--aria-primary)] inline-flex items-center gap-1">
                  {starting === c.concept ? <Loader2 size={12} className="animate-spin" /> : <Mic2 size={12} />} Drill
                </span>
              </Row>
            ))}
          </Section>

          {/* Continue learning */}
          <Section title="Continue learning" icon={GraduationCap} testid="section-continue">
            {(dash?.recent_videos || []).length === 0 && (dash?.recent_notes || []).length === 0 && <Empty text="Connect a lecture or note to get started." />}
            {(dash?.recent_videos || []).map(v => (
              <Row key={v.video_id} testid={`recent-video-${v.video_id}`} onClick={() => navigate("/app/learn")}>
                <Youtube size={14} className="text-[var(--aria-primary)]" />
                <div className="flex-1"><div className="font-medium text-sm truncate">{v.title}</div></div>
              </Row>
            ))}
            {(dash?.recent_notes || []).map(n => (
              <Row key={n.note_id} testid={`recent-note-${n.note_id}`} onClick={() => navigate("/app/notes")}>
                <StickyNote size={14} className="text-[var(--aria-primary)]" />
                <div className="flex-1"><div className="font-medium text-sm truncate">{n.title}</div></div>
              </Row>
            ))}
          </Section>

          {/* Research */}
          <Section title="Research" icon={TrendingUp} testid="section-research">
            {(dash?.recent_interviews || []).length === 0 && <Empty text="No interviews run yet — try the Interview Center." />}
            {(dash?.recent_interviews || []).map(i => (
              <Row key={i.interview_id} testid={`recent-interview-${i.interview_id}`} onClick={() => navigate(`/app/interviews/${i.interview_id}`)}>
                <Mic2 size={14} className="text-[var(--aria-primary)]" />
                <div className="flex-1">
                  <div className="font-medium text-sm truncate">{i.target}</div>
                  <div className="text-xs text-[var(--aria-text-muted)]">{i.status} · {i.gaps_detected?.length || 0} gap(s) found</div>
                </div>
              </Row>
            ))}
          </Section>
        </div>

        <WeeklyReport />

        <div className="mt-10 text-xs text-[var(--aria-text-muted)] font-mono flex items-center gap-2" data-testid="connected-summary">
          <Clock size={12} /> {totalConnected} sources connected · {dash?.counts?.concepts || 0} concepts tracked
        </div>
      </div>
    </div>
  );
}

function CountdownCard() {
  const navigate = useNavigate();
  const { plan, loading, generating, generate, toggle } = usePlan();
  const [streakKey, setStreakKey] = useState(0);
  const toggleTask = async (id) => { await toggle(id); setStreakKey(k => k + 1); };
  if (loading) return null;
  const today = new Date().toISOString().slice(0, 10);
  const day = plan?.days?.find(d => d.date === today) || plan?.days?.find(d => d.date > today);
  const prog = planProgress(plan);
  const interviewDay = plan && plan.days_left <= 1;
  return (
    <>
    {interviewDay && (
      <button onClick={() => navigate("/app/warmup")} data-testid="interview-day-banner"
        className="w-full aria-card aria-card-accent p-4 mb-4 flex items-center gap-3 text-left ring-glow-primary">
        <Sunrise size={18} className="text-[var(--aria-primary)]" />
        <div className="flex-1">
          <div className="font-display text-lg">{plan.days_left === 0 ? "It's interview day." : "Interview is tomorrow."}</div>
          <div className="text-xs text-[var(--aria-text-muted)]">Take the calm 15-minute warm-up — your closed gaps and strongest answers, replayed.</div>
        </div>
        <ArrowRight size={16} className="text-[var(--aria-primary)]" />
      </button>
    )}
    <div className="aria-card p-5 mb-6 flex items-start gap-5 flex-wrap" data-testid="countdown-card">
      <div className="flex items-center gap-4 min-w-[200px]">
        <Timer size={18} className="text-[var(--aria-primary)]" />
        {plan ? (
          <div>
            <div className="font-display text-3xl leading-none text-[var(--aria-primary)]" data-testid="countdown-days">{plan.days_left}<span className="text-sm text-[var(--aria-text-muted)] ml-1.5">days</span></div>
            <div className="text-xs text-[var(--aria-text-muted)] mt-1 truncate max-w-[220px]">until {plan.deadline_title}</div>
          </div>
        ) : (
          <div>
            <div className="font-display text-lg">Exam countdown</div>
            <div className="text-xs text-[var(--aria-text-muted)]">Build a day-by-day plan to your next interview.</div>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-[260px]">
        {plan && day ? (
          <>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-1.5">{day.date === today ? "Today's plan" : `Next up · ${day.label}`} · {prog.done}/{prog.total} done</div>
            <div className="space-y-1">
              {day.tasks.slice(0, 4).map(t => (
                <button key={t.id} onClick={() => toggleTask(t.id)} data-testid={`countdown-task-${t.id}`}
                  className={`w-full flex items-center gap-2 text-sm text-left px-2 py-1 rounded hover:bg-[var(--aria-surface-2)] ${t.done ? "opacity-50 line-through" : ""}`}>
                  {t.done ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0" /> : <Circle size={14} className="text-[var(--aria-text-muted)] shrink-0" />}
                  <span className="truncate">{t.text}</span>
                </button>
              ))}
            </div>
          </>
        ) : plan ? (
          <div className="text-sm text-[var(--aria-text-muted)]">Plan complete — it's interview day. Good luck.</div>
        ) : null}
      </div>
      {plan && <div className="self-center"><StreakRing refreshKey={streakKey} /></div>}
      <div className="self-center flex flex-col items-end gap-2">
        {plan ? (
          <>
            <button onClick={() => navigate("/app/plan")} data-testid="countdown-open-button" className="text-sm text-[var(--aria-primary)] inline-flex items-center gap-1.5 hover:underline">Full plan <ArrowRight size={14} /></button>
            <button onClick={() => navigate("/app/warmup")} data-testid="countdown-warmup-link" className="text-xs text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] inline-flex items-center gap-1.5"><Sunrise size={12} /> Interview-day warm-up</button>
          </>
        ) : (
          <button onClick={generate} disabled={generating} data-testid="countdown-build-button" className="btn btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Timer size={14} />} Build my plan
          </button>
        )}
      </div>
    </div>
    </>
  );
}

function WeeklyReport() {
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async (refresh = false) => {
    setBusy(true);
    try { const { data } = await api.get(`/home/weekly-report${refresh ? "?refresh=true" : ""}`); setReport(data); }
    catch (err) { console.error("[ARIA]", err); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async () => {
    try {
      const resp = await api.get("/home/weekly-report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `aria-weekly-report-${report?.week || "week"}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Weekly report PDF downloaded");
    } catch { toast.error("Could not export the report"); }
  };

  const s = report?.stats || {};
  return (
    <div className="aria-card p-5 mt-6" data-testid="weekly-report">
      <button onClick={() => setOpen(!open)} data-testid="weekly-report-toggle" className="w-full flex items-center justify-between text-left">
        <div className="flex items-center gap-2">
          <FileBarChart size={15} className="text-[var(--aria-primary)]" />
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--aria-text-muted)]">Weekly intelligence report</h3>
          {report?.week && <span className="aria-pill text-[10px]">{report.week}</span>}
        </div>
        <ChevronDown size={14} className={`text-[var(--aria-text-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4 fade-in">
          {busy && !report ? <div className="text-sm text-[var(--aria-text-muted)]">Compiling your week…</div> : (
            <>
              <p className="text-[15px] leading-relaxed whitespace-pre-line" data-testid="weekly-report-text">{report?.text}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <Stat label="Interviews" value={s.interviews ?? 0} />
                <Stat label="Gaps found" value={s.gaps_found ?? 0} />
                <Stat label="Gaps closed" value={s.gaps_closed ?? 0} accent />
                <Stat label="Sources added" value={s.new_sources ?? 0} />
              </div>
              {(s.focus_minutes ?? 0) > 0 && (
                <div className="mt-3 text-xs text-[var(--aria-text-muted)]" data-testid="weekly-focus-minutes">
                  <span className="text-[var(--aria-text)] font-semibold">{s.focus_minutes} focused min</span> across {s.focus_sessions} session{s.focus_sessions === 1 ? "" : "s"}
                  {report.focus_minutes?.length > 0 && <> · {report.focus_minutes.slice(0, 4).map(f => `${f.concept} ${f.minutes}m`).join(" · ")}</>}
                </div>
              )}
              {report?.practiced?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="weekly-practiced">
                  {report.practiced.map(p => (
                    <span key={p.concept} className="aria-pill text-xs">{p.concept} · {LEVEL_LABEL[p.level] || p.level}</span>
                  ))}
                </div>
              )}
              <div className="mt-4 flex items-center gap-4">
                <button onClick={() => load(true)} disabled={busy} data-testid="weekly-report-refresh"
                  className="text-xs text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] inline-flex items-center gap-1.5 disabled:opacity-50">
                  <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> Regenerate
                </button>
                {report?.has_data && (
                  <button onClick={downloadPdf} data-testid="weekly-report-pdf"
                    className="text-xs text-[var(--aria-primary)] hover:underline inline-flex items-center gap-1.5">
                    <FileDown size={11} /> Save as PDF
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-md bg-[var(--aria-surface-2)] px-3 py-2.5">
      <div className={`font-display text-2xl ${accent ? "text-emerald-400" : ""}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-[var(--aria-text-muted)]">{label}</div>
    </div>
  );
}

function ActionCard({ icon: Icon, title, text, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="aria-card aria-magnetic p-6 text-left group">
      <div className="w-10 h-10 rounded-md bg-[var(--aria-primary)]/10 flex items-center justify-center mb-4 group-hover:bg-[var(--aria-primary)]/20 transition-colors">
        <Icon size={20} className="text-[var(--aria-primary)]" />
      </div>
      <h3 className="font-display text-lg mb-1">{title}</h3>
      <p className="text-sm text-[var(--aria-text-muted)]">{text}</p>
    </button>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="aria-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={15} className="text-[var(--aria-primary)]" />
        <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--aria-text-muted)]">{title}</h3>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ children, onClick, testid }) {
  return (
    <div onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md ${onClick ? "cursor-pointer hover:bg-[var(--aria-surface-2)]" : ""}`}>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-[var(--aria-text-muted)] py-3 px-2.5">{text}</div>;
}
